import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

const IMAGE_VIEW_KEY = 'product-image-view';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('paginated-list:new-elements', this.#applyImageViewToNewElements);
    this.setAttribute('initialized', '');
    const ghostSwitcherEnabled = this.getAttribute('data-ghost-switcher-enabled') !== 'false';
    const storedImageView = ghostSwitcherEnabled
      ? (sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model')
      : 'model';
    if (!ghostSwitcherEnabled) sessionStorage.setItem(IMAGE_VIEW_KEY, 'model');
    requestIdleCallback(() => {
      this.#setImageView(storedImageView);
      const radio = this.querySelector(
        `input[type="radio"][name="product-image-view"][value="${storedImageView}"]`
      );
      if (radio instanceof HTMLInputElement) radio.checked = true;
      const toggle = this.querySelector('input[data-image-view-toggle]');
      if (toggle instanceof HTMLInputElement) toggle.checked = storedImageView === 'ghost';
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('paginated-list:new-elements', this.#applyImageViewToNewElements);
  }

  /** Re-apply current image view (model/ghost) when load more adds new cards. Only processes new elements to avoid O(n) over whole grid. */
  #applyImageViewToNewElements = (event) => {
    const ghostSwitcherEnabled = this.getAttribute('data-ghost-switcher-enabled') !== 'false';
    const value = ghostSwitcherEnabled ? (sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model') : 'model';
    const newElements = event?.detail?.elements;
    const scope = Array.isArray(newElements) && newElements.length > 0 ? newElements : null;
    this.#setImageView(value, scope);
    requestAnimationFrame(() => {
      this.#setImageView(value, scope);
      setTimeout(() => this.#setImageView(value, scope), 50);
    });
  };

  updatedCallback() {
    super.updatedCallback();
    const value = sessionStorage.getItem(IMAGE_VIEW_KEY) || 'model';
    requestIdleCallback(() => this.#setImageView(value));
  }

  /**
   * Updates the image view (Model / Ghost). Called from image-view-controls (radio variant).
   * @param {Event} event - change event from radio input
   */
  updateImageView({ target }) {
    if (!(target instanceof HTMLInputElement) || !target.value) return;
    const value = target.value === 'ghost' ? 'ghost' : 'model';
    sessionStorage.setItem(IMAGE_VIEW_KEY, value);
    this.#setImageView(value);
    this.#pushImageViewEvent(value);
  }

  /**
   * Updates the image view from the Ghost toggle (checkbox). Checked = ghost, unchecked = model.
   * @param {Event} event - change event from checkbox
   */
  updateImageViewFromToggle({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    const value = target.checked ? 'ghost' : 'model';
    sessionStorage.setItem(IMAGE_VIEW_KEY, value);
    this.#setImageView(value);
    this.#pushImageViewEvent(value);
  }

  /**
   * Pushes image_view_change to GA (gtag) and/or GTM (dataLayer).
   * @param {string} view - 'model' | 'ghost'
   */
  #pushImageViewEvent(view) {
    const params = { image_view: view };
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'image_view_change', params);
    }
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'image_view_change', ...params });
    }
  }

  /**
   * Sets product-image-view on grid and swaps card images (model vs ghost).
   * Uses the first slide (card cover) so video covers are handled reliably.
   * @param {string} value - 'model' | 'ghost'
   * @param {Element[] | null} [scopeElements] - If set, only process galleries inside these elements (e.g. newly loaded cards); otherwise processes all.
   */
  #setImageView(value, scopeElements = null) {
    const grid = this.refs?.grid ?? this.querySelector('[ref="grid"]');
    if (grid) grid.setAttribute('product-image-view', value);
    const galleries =
      scopeElements?.length > 0
        ? scopeElements.flatMap((el) => Array.from(el.querySelectorAll('.card-gallery[data-ghost-src]')))
        : Array.from(this.querySelectorAll('.card-gallery[data-ghost-src]'));
    const useGhost = value === 'ghost';
    galleries.forEach((gallery) => {
      const firstSlide = gallery.querySelector('slideshow-slide');
      const container = firstSlide ?? gallery;
      const video = container.querySelector('video');
      const modelSrc = gallery.dataset.modelSrc;
      const ghostSrc = gallery.dataset.ghostSrc;

      if (video && ghostSrc) {
        this.#setImageViewForVideoCover(video, ghostSrc, useGhost);
      } else {
        const img = container.querySelector('img');
        if (img && !img.closest('video')) {
          this.#setImageViewForImage(img, gallery, modelSrc, ghostSrc, useGhost);
        }
      }
    });
  }

  /**
   * Swaps a single card's image (model vs ghost). Clears srcset when showing ghost.
   */
  #setImageViewForImage(img, gallery, modelSrc, ghostSrc, useGhost) {
    if (useGhost && ghostSrc) {
      if (!img.dataset.originalSrc) {
        img.dataset.originalSrc = img.currentSrc || img.getAttribute('src') || modelSrc || '';
        const srcset = img.getAttribute('srcset');
        if (srcset) img.dataset.originalSrcset = srcset;
      }
      img.setAttribute('src', ghostSrc);
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
    } else {
      const restoreSrc = img.dataset.originalSrc || modelSrc || img.getAttribute('src');
      img.setAttribute('src', restoreSrc);
      if (img.dataset.originalSrcset) {
        img.setAttribute('srcset', img.dataset.originalSrcset);
        delete img.dataset.originalSrcset;
      }
      delete img.dataset.originalSrc;
    }
  }

  /**
   * When the card cover is a video: in ghost mode show ghost image and hide video; in model mode show video and hide ghost img.
   */
  #setImageViewForVideoCover(video, ghostSrc, useGhost) {
    const mediaContainer = video.closest('.product-media') ?? video.parentElement;
    if (!mediaContainer) return;

    let ghostImg = mediaContainer.querySelector('.card-gallery__ghost-overlay');
    if (useGhost && ghostSrc) {
      if (!ghostImg) {
        ghostImg = document.createElement('img');
        ghostImg.src = ghostSrc;
        ghostImg.alt = '';
        ghostImg.loading = 'lazy';
        ghostImg.decoding = 'async';
        ghostImg.className = 'product-media__image card-gallery__ghost-overlay';
        ghostImg.setAttribute('data-ghost-overlay', '');
        mediaContainer.style.position = 'relative';
        mediaContainer.prepend(ghostImg);
      } else {
        ghostImg.src = ghostSrc;
        ghostImg.hidden = false;
        ghostImg.style.display = '';
      }
      video.hidden = true;
      video.style.setProperty('display', 'none', 'important');
    } else {
      if (ghostImg) {
        ghostImg.hidden = true;
        ghostImg.style.display = 'none';
      }
      video.hidden = false;
      video.style.removeProperty('display');
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    try {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    } catch (_e) {
      // If View Transitions fail (unsupported browser / script interference), still apply the layout.
      this.#setLayout(value);
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    const inputName = viewport === 'desktop' ? 'grid' : 'grid-mobile';
    const targetElement = this.querySelector(
      `input[type="radio"][name="${inputName}"][value="${storedLayout}"]`
    );

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#setLayout(storedLayout);
      return;
    }

    // Fallback: if no matching option exists for this viewport, reset to default.
    const fallback = this.querySelector(
      viewport === 'desktop' ? '[data-grid-layout="desktop-default-option"]' : '[data-grid-layout="mobile-option"]'
    );
    if (fallback instanceof HTMLInputElement) {
      fallback.checked = true;
    }
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
